import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";

import {
  CardContent,
  Box,
  BoxProps,
  Fade,
  Stack,
  styled,
  Typography,
  Theme,
  useTheme,
  Card,
  CardProps,
} from "@mui/material";

export enum MessageType {
  Success,
  Warning,
  Error,
  Info,
}

interface Props extends BoxProps {
  messageType: MessageType;
  title?: string;
  content?: string;
  primaryText?: string;
  secondaryText?: string | JSX.Element;
  cardActions?: JSX.Element;
  enableColoredBorder?: boolean;
  highlightMessage?: boolean;
  onMessageHighlightComplete?: () => void;
  cardProps?: CardProps;
}

function getColorByMessageType(messageType: MessageType, theme: Theme) {
  switch (messageType) {
    case MessageType.Error:
      return theme.palette.error.main;
    case MessageType.Info:
      return theme.palette.info.main;
    case MessageType.Success:
      return theme.palette.success.main;
    case MessageType.Warning:
      return theme.palette.warning.main;
  }
}

function getIconByMessageType(messageType: MessageType) {
  switch (messageType) {
    case MessageType.Error:
      return faExclamationTriangle;
    case MessageType.Info:
      return faInfoCircle;
    case MessageType.Success:
      return faCheckCircle;
    case MessageType.Warning:
      return faExclamationTriangle;
  }
}

const IconContainer = styled("span")(({ theme }) => {
  return {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  };
});

export function MessageCard(props: Props) {
  const theme = useTheme();

  const [fadingCard, setFadingCard] = useState(false);
  const [highlightingBorder, setHighlightingBorder] = useState(false);

  const {
    messageType,
    title,
    primaryText,
    secondaryText,
    cardActions,
    enableColoredBorder,
    highlightMessage,
    onMessageHighlightComplete,
    cardProps,
    ...boxProps
  } = props;

  useEffect(() => {
    if (highlightMessage) {
      setHighlightingBorder(true);
      setFadingCard(true);

      const borderHighlightTimeout = setTimeout(() => {
        setHighlightingBorder(false);
        onMessageHighlightComplete?.();
      }, theme.transitions.duration.complex);

      const cardFadeTimeout = setTimeout(() => setFadingCard(false), theme.transitions.duration.shorter);

      return () => {
        clearTimeout(borderHighlightTimeout);
        clearTimeout(cardFadeTimeout);
      };
    }
  }, [
    highlightMessage,
    onMessageHighlightComplete,
    theme.transitions.duration.complex,
    theme.transitions.duration.shorter,
  ]);

  return (
    <Fade in={!fadingCard} timeout={theme.transitions.duration.shorter}>
      <Box display="flex" justifyContent="center" {...boxProps}>
        <Card
          {...cardProps}
          sx={{
            width: 285,
            borderColor: (theme) =>
              enableColoredBorder || highlightingBorder ? getColorByMessageType(messageType, theme) : undefined,
            transition: (theme) =>
              theme.transitions.create(["border-color", "transform"], {
                duration: theme.transitions.duration.complex,
              }),
            ...cardProps?.sx,
          }}
        >
          <CardContent>
            <Stack spacing={2} display="flex" justifyContent="center" alignItems="center">
              <IconContainer>
                <FontAwesomeIcon
                  icon={getIconByMessageType(messageType)}
                  size="3x"
                  color={getColorByMessageType(messageType, theme)}
                />
              </IconContainer>
              {title && (
                <Typography color="textPrimary" gutterBottom variant="h6" fontSize={"1em"} align="center">
                  {title}
                </Typography>
              )}
              {props.content && (
                <Typography color="textPrimary" gutterBottom variant="subtitle2" component="div" align="center">
                  {props.content}
                </Typography>
              )}
              {primaryText && (
                <Typography color="textPrimary" gutterBottom variant="subtitle2" align="center">
                  {primaryText}
                </Typography>
              )}
              {secondaryText && (
                <Typography variant="caption" color="textSecondary" align="center">
                  {secondaryText}
                </Typography>
              )}

              {cardActions}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
}
